import type { CmmdDeviceRegistryDevice, CmmdDeviceRegistryRuntime } from "@cmmd-forge/contracts";

import type { DesktopDiagnosticsTab } from "./DiagnosticsSettings.types";

export type DesktopRegistryPresence = "online" | "offline";

export interface DesktopDiagnosticsTarget {
  readonly tabId: DesktopDiagnosticsTab;
  readonly runtimeId: string;
  readonly deviceId: number;
  readonly label: string;
  readonly platform: string;
  readonly observedAt: string | null;
  /** Account-scoped presence as CMMD's registry last recorded it. */
  readonly presence: DesktopRegistryPresence;
  readonly source: "cmmd_device_registry";
  readonly collectionOutcome: "registry_online" | "registry_offline";
}

function isAuthorizedDesktopRuntime(
  runtime: CmmdDeviceRegistryRuntime,
  device: CmmdDeviceRegistryDevice | undefined,
): device is CmmdDeviceRegistryDevice {
  return (
    runtime.source === "registered" &&
    runtime.runtimeKind === "desktop-local" &&
    runtime.deviceId !== null &&
    device?.trustState === "trusted" &&
    device.authorization.bound &&
    !device.authorization.revoked
  );
}

function desktopDiagnosticsTabId(runtimeId: string): DesktopDiagnosticsTab {
  return `desktop:${runtimeId}`;
}

export function isDesktopDiagnosticsTab(tab: string): tab is DesktopDiagnosticsTab {
  return tab.startsWith("desktop:");
}

/**
 * The registry can hold several runtime rows for one machine (one per
 * registration). Connections collapses them to one row per host, and the
 * picker has to agree or the same server shows up twice.
 */
function desktopHostKey(runtime: CmmdDeviceRegistryRuntime): string {
  if (typeof runtime.hostId === "number") return `host:${runtime.hostId}`;
  if (runtime.deviceId !== null) return `device:${runtime.deviceId}`;
  return `runtime:${runtime.id}`;
}

/**
 * CMMD reachability is the presence contract; the legacy status string is
 * only read for cached rows written before reachability shipped. `suspect`
 * is not online: the relay lease has lapsed and nothing has proven otherwise.
 */
function desktopPresence(runtime: CmmdDeviceRegistryRuntime): DesktopRegistryPresence {
  const status = runtime.reachability?.status ?? runtime.status;
  return status === "online" ? "online" : "offline";
}

/**
 * One target per trusted Forge Desktop server, online or not.
 *
 * Registry `online` is account-scoped connection presence and never stands in
 * for a remote process or log snapshot. An offline server still belongs in the
 * list: it is the user's machine, Connections shows it, and the canonical
 * no-wake diagnostics read reports its last observation either way. Dropping
 * it made the picker say "no desktop servers" about a laptop that was closed.
 */
export function makeDesktopDiagnosticsTargets(input: {
  readonly runtimes: ReadonlyArray<CmmdDeviceRegistryRuntime>;
  readonly devices: ReadonlyArray<CmmdDeviceRegistryDevice>;
}): ReadonlyArray<DesktopDiagnosticsTarget> {
  const deviceById = new Map(input.devices.map((device) => [device.id, device]));
  const seenHosts = new Set<string>();
  return input.runtimes.flatMap((runtime) => {
    const device = runtime.deviceId === null ? undefined : deviceById.get(runtime.deviceId);
    if (!isAuthorizedDesktopRuntime(runtime, device)) return [];
    const hostKey = desktopHostKey(runtime);
    if (seenHosts.has(hostKey)) return [];
    seenHosts.add(hostKey);
    const presence = desktopPresence(runtime);
    return [
      {
        tabId: desktopDiagnosticsTabId(runtime.id),
        runtimeId: runtime.id,
        deviceId: device.id,
        label: runtime.displayName,
        platform: device.platform,
        observedAt: runtime.lastSeenAt ?? device.lastSeenAt,
        presence,
        source: "cmmd_device_registry" as const,
        collectionOutcome:
          presence === "online" ? ("registry_online" as const) : ("registry_offline" as const),
      },
    ];
  });
}
